/**
 * Layout for the SQ3R worksheet: two portrait pages. Page one holds the header, the
 * Survey checklist and the Question table; page two holds Read and Recite (an answer
 * box for every question) and Review (summary, gaps and review dates).
 *
 * Positions are millimetres on a page from `pageGeometry`. The layout takes strings
 * already in the reader's language and returns drawing primitives.
 */

import { type PageGeometry, r2 } from "@/lib/tool-kit/printables/page";
import type { PlacedText } from "@/lib/tool-kit/printables/reward-chart";
import {
  type FontSpec,
  fitSize,
  type TextMeasure,
  truncateToFit,
} from "@/lib/tool-kit/printables/text";
import type {
  PlannerBox,
  PlannerLine,
  PlannerPage,
} from "@/lib/tool-kit/study-more/weekly-planner";

const BOLD: FontSpec = { kind: "sans", bold: true };
const SANS: FontSpec = { kind: "sans" };

export const MAX_HEADINGS = 12;
export const MIN_ROWS = 3;

export interface Sq3rHeader {
  name: string;
  course: string;
  reading: string;
  pages: string;
  date: string;
}

export interface Sq3rLabels {
  title: string;
  name: string;
  course: string;
  reading: string;
  pages: string;
  date: string;
  survey: {
    title: string;
    hint: string;
    checks: readonly string[];
    topic: string;
  };
  question: { title: string; hint: string; heading: string; mine: string };
  read: { title: string; hint: string };
  recite: { title: string; hint: string; answer: string; check: string };
  review: {
    title: string;
    hint: string;
    summary: string;
    unclear: string;
    again: string;
    dates: readonly string[];
  };
}

export interface Sq3rOptions {
  header: Sq3rHeader;
  /** The headings typed by the reader; empty strings are blank rows. */
  headings: readonly string[];
  labels: Sq3rLabels;
}

/** The headings to lay out: typed ones, cut to the limit, or `blankRows` empty rows. */
export function sq3rRows(
  typed: readonly string[],
  blankRows: number
): string[] {
  const headings = typed
    .map((heading) => heading.trim())
    .filter((heading) => heading.length > 0)
    .slice(0, MAX_HEADINGS);
  if (headings.length > 0) {
    return headings;
  }
  const count = Math.min(MAX_HEADINGS, Math.max(MIN_ROWS, blankRows));
  return Array.from({ length: count }, () => "");
}

function emptyPage(): PlannerPage {
  return { texts: [], lines: [], boxes: [], checks: [] };
}

function text(
  value: string,
  x: number,
  y: number,
  size: number,
  anchor: PlacedText["anchor"] = "start",
  bold = false
): PlacedText {
  return { text: value, x: r2(x), y: r2(y), size: r2(size), anchor, bold };
}

function rule(
  x1: number,
  y: number,
  x2: number,
  weight: PlannerLine["weight"] = "normal"
): PlannerLine {
  return { x1: r2(x1), y1: r2(y), x2: r2(x2), y2: r2(y), weight };
}

function rect(x: number, y: number, width: number, height: number): PlannerBox {
  return { x: r2(x), y: r2(y), width: r2(width), height: r2(height) };
}

/** A "label ______" field: the label, a writing line, and the typed value on it. */
function field(
  page: PlannerPage,
  measure: TextMeasure,
  label: string,
  value: string,
  x: number,
  y: number,
  width: number
): void {
  const labelSize = 3.6;
  const labelWidth = measure(label, SANS) * labelSize + 1.5;
  page.texts.push(text(label, x, y, labelSize));
  page.lines.push(rule(x + labelWidth, y + 0.8, x + width));
  if (value.trim()) {
    const room = width - labelWidth - 1;
    const shown = truncateToFit(measure, value.trim(), BOLD, 4, room);
    page.texts.push(text(shown, x + labelWidth + 1, y - 0.2, 4, "start", true));
  }
}

/** A section banner: a boxed letter, the step's name in bold, and its instruction. */
function banner(
  page: PlannerPage,
  geometry: PageGeometry,
  measure: TextMeasure,
  letter: string,
  title: string,
  hint: string,
  y: number
): number {
  const left = geometry.left;
  page.boxes.push(rect(left, y, 8, 8));
  page.texts.push(text(letter, left + 4, y + 6, 5.6, "middle", true));
  page.texts.push(text(title, left + 11, y + 3.6, 4.4, "start", true));
  const titleWidth = measure(title, BOLD) * 4.4;
  const hintSize = fitSize(
    measure,
    hint,
    SANS,
    geometry.contentWidth - 11 - titleWidth - 4,
    3.2,
    2.4
  );
  page.texts.push(
    text(
      truncateToFit(
        measure,
        hint,
        SANS,
        hintSize,
        geometry.contentWidth - 11 - titleWidth - 4
      ),
      left + 11 + titleWidth + 3,
      y + 3.6,
      hintSize
    )
  );
  page.lines.push(
    rule(left + 11, y + 6.6, left + geometry.contentWidth, "faint")
  );
  return y + 11;
}

function headerBlock(
  page: PlannerPage,
  geometry: PageGeometry,
  measure: TextMeasure,
  options: Sq3rOptions
): number {
  const { labels, header } = options;
  const left = geometry.left;
  const width = geometry.contentWidth;
  const size = fitSize(measure, labels.title, BOLD, width, 8, 4);
  page.texts.push(
    text(labels.title, left, geometry.top + 7, size, "start", true)
  );
  const row1 = geometry.top + 17;
  field(page, measure, labels.name, header.name, left, row1, width * 0.48);
  field(
    page,
    measure,
    labels.course,
    header.course,
    left + width * 0.52,
    row1,
    width * 0.48
  );
  const row2 = row1 + 8.5;
  field(page, measure, labels.reading, header.reading, left, row2, width * 0.5);
  field(
    page,
    measure,
    labels.pages,
    header.pages,
    left + width * 0.54,
    row2,
    width * 0.2
  );
  field(
    page,
    measure,
    labels.date,
    header.date,
    left + width * 0.78,
    row2,
    width * 0.22
  );
  return row2 + 7;
}

function surveySection(
  page: PlannerPage,
  geometry: PageGeometry,
  measure: TextMeasure,
  options: Sq3rOptions,
  top: number
): number {
  const { survey } = options.labels;
  let y = banner(page, geometry, measure, "S", survey.title, survey.hint, top);
  const half = geometry.contentWidth / 2;
  survey.checks.forEach((label, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const x = geometry.left + 2 + column * half;
    const cy = y + 3 + row * 6;
    page.checks.push(rect(x, cy - 3, 3.6, 3.6));
    page.texts.push(
      text(
        truncateToFit(measure, label, SANS, 3.5, half - 10),
        x + 5.5,
        cy,
        3.5
      )
    );
  });
  y += Math.ceil(survey.checks.length / 2) * 6 + 2;
  field(
    page,
    measure,
    survey.topic,
    "",
    geometry.left + 2,
    y + 3,
    geometry.contentWidth - 2
  );
  page.lines.push(
    rule(
      geometry.left + 2,
      y + 11.5,
      geometry.left + geometry.contentWidth,
      "faint"
    )
  );
  return y + 16;
}

function questionTable(
  page: PlannerPage,
  geometry: PageGeometry,
  measure: TextMeasure,
  options: Sq3rOptions,
  rows: readonly string[],
  top: number
): void {
  const { question } = options.labels;
  const y = banner(
    page,
    geometry,
    measure,
    "Q",
    question.title,
    question.hint,
    top
  );
  const left = geometry.left;
  const right = left + geometry.contentWidth;
  const bottom = geometry.top + geometry.contentHeight;
  const numberWidth = 8;
  const headingWidth = Math.min(70, geometry.contentWidth * 0.36);
  const headRow = 7;
  const rowHeight = Math.min(18, (bottom - y - headRow) / rows.length);
  const tableHeight = headRow + rowHeight * rows.length;
  page.boxes.push(rect(left, y, geometry.contentWidth, tableHeight));
  page.lines.push(rule(left, y + headRow, right, "normal"));
  const cut1 = left + numberWidth;
  const cut2 = cut1 + headingWidth;
  for (const x of [cut1, cut2]) {
    page.lines.push({
      x1: r2(x),
      y1: r2(y),
      x2: r2(x),
      y2: r2(y + tableHeight),
      weight: "normal",
    });
  }
  page.texts.push(
    text(question.heading, cut1 + 2, y + 4.8, 3.4, "start", true),
    text(question.mine, cut2 + 2, y + 4.8, 3.4, "start", true)
  );
  rows.forEach((heading, index) => {
    const top2 = y + headRow + rowHeight * index;
    if (index > 0) {
      page.lines.push(rule(left, top2, right, "faint"));
    }
    page.texts.push(
      text(
        String(index + 1),
        left + numberWidth / 2,
        top2 + 5,
        3.6,
        "middle",
        true
      )
    );
    if (heading) {
      page.texts.push(
        text(
          truncateToFit(measure, heading, SANS, 3.6, headingWidth - 4),
          cut1 + 2,
          top2 + 5,
          3.6
        )
      );
    }
  });
}

function reciteTable(
  page: PlannerPage,
  geometry: PageGeometry,
  measure: TextMeasure,
  options: Sq3rOptions,
  rows: readonly string[],
  top: number,
  bottom: number
): void {
  const { read, recite } = options.labels;
  let y = banner(page, geometry, measure, "R", read.title, read.hint, top);
  y = banner(page, geometry, measure, "R", recite.title, recite.hint, y - 1);
  const left = geometry.left;
  const right = left + geometry.contentWidth;
  const numberWidth = 8;
  const checkWidth = 16;
  const questionWidth = Math.min(46, geometry.contentWidth * 0.24);
  const headRow = 7;
  const rowHeight = Math.min(22, (bottom - y - headRow) / rows.length);
  const tableHeight = headRow + rowHeight * rows.length;
  page.boxes.push(rect(left, y, geometry.contentWidth, tableHeight));
  page.lines.push(rule(left, y + headRow, right, "normal"));
  const cut1 = left + numberWidth;
  const cut2 = cut1 + questionWidth;
  const cut3 = right - checkWidth;
  for (const x of [cut1, cut2, cut3]) {
    page.lines.push({
      x1: r2(x),
      y1: r2(y),
      x2: r2(x),
      y2: r2(y + tableHeight),
      weight: "normal",
    });
  }
  page.texts.push(
    text(
      options.labels.question.heading,
      cut1 + 2,
      y + 4.8,
      3.4,
      "start",
      true
    ),
    text(recite.answer, cut2 + 2, y + 4.8, 3.4, "start", true),
    text(recite.check, cut3 + checkWidth / 2, y + 4.8, 3.2, "middle", true)
  );
  rows.forEach((heading, index) => {
    const rowTop = y + headRow + rowHeight * index;
    if (index > 0) {
      page.lines.push(rule(left, rowTop, right, "faint"));
    }
    page.texts.push(
      text(
        String(index + 1),
        left + numberWidth / 2,
        rowTop + 5,
        3.6,
        "middle",
        true
      )
    );
    if (heading) {
      page.texts.push(
        text(
          truncateToFit(measure, heading, SANS, 3.4, questionWidth - 4),
          cut1 + 2,
          rowTop + 5,
          3.4
        )
      );
    }
    for (let ruled = rowTop + 8; ruled < rowTop + rowHeight - 1; ruled += 7) {
      page.lines.push(rule(cut2 + 2, ruled, cut3 - 2, "faint"));
    }
    page.checks.push(
      rect(cut3 + checkWidth / 2 - 2, rowTop + rowHeight / 2 - 2, 4, 4)
    );
  });
}

function reviewSection(
  page: PlannerPage,
  geometry: PageGeometry,
  measure: TextMeasure,
  options: Sq3rOptions,
  top: number
): void {
  const { review } = options.labels;
  const left = geometry.left;
  const right = left + geometry.contentWidth;
  let y = banner(page, geometry, measure, "R", review.title, review.hint, top);
  page.texts.push(text(review.summary, left + 2, y + 3, 3.6, "start", true));
  for (let row = 1; row <= 4; row += 1) {
    page.lines.push(rule(left + 2, y + 3 + row * 6.5, right, "faint"));
  }
  y += 3 + 4 * 6.5 + 4;
  page.texts.push(text(review.unclear, left + 2, y + 3, 3.6, "start", true));
  for (let row = 1; row <= 2; row += 1) {
    page.lines.push(rule(left + 2, y + 3 + row * 6.5, right, "faint"));
  }
  y += 3 + 2 * 6.5 + 5;
  page.texts.push(text(review.again, left + 2, y + 3, 3.6, "start", true));
  let x = left + 2 + measure(review.again, BOLD) * 3.6 + 4;
  for (const label of review.dates) {
    page.checks.push(rect(x, y, 3.6, 3.6));
    page.texts.push(text(label, x + 5.4, y + 3, 3.5));
    x += 5.4 + measure(label, SANS) * 3.5 + 6;
  }
}

/** Both pages of the worksheet. */
export function layoutSq3r(
  geometry: PageGeometry,
  measure: TextMeasure,
  options: Sq3rOptions
): [PlannerPage, PlannerPage] {
  const rows =
    options.headings.length > 0 ? options.headings : sq3rRows([], MIN_ROWS);
  const first = emptyPage();
  let y = headerBlock(first, geometry, measure, options);
  y = surveySection(first, geometry, measure, options, y);
  questionTable(first, geometry, measure, options, rows, y);
  const second = emptyPage();
  const bottom = geometry.top + geometry.contentHeight;
  const reviewHeight = 11 + 3 + 4 * 6.5 + 4 + 3 + 2 * 6.5 + 5 + 6;
  reciteTable(
    second,
    geometry,
    measure,
    options,
    rows,
    geometry.top,
    bottom - reviewHeight - 3
  );
  reviewSection(second, geometry, measure, options, bottom - reviewHeight);
  return [first, second];
}
